/**
 * Derive a short timezone abbreviation (e.g. "PT", "CST", "IST") from a Date object.
 * Uses Intl.DateTimeFormat with timeZoneName:"short" which returns standard abbreviations
 * like "PT", "CST", "IST". Falls back to parsing toTimeString() parenthesised name
 * (e.g. "India Standard Time" → "IST") if Intl returns a raw GMT offset.
 */
const getTimezoneAbbr = (d: Date): string => {
  try {
    // Primary: use Intl which returns proper abbreviations (PT, CST, IST, etc.)
    const rawShort = new Intl.DateTimeFormat("en-US", { timeZoneName: "short" })
      .formatToParts(d)
      .find((pt) => pt.type === "timeZoneName")?.value ?? "";

    // Use it if it's a real abbreviation (not a bare "GMT" or "UTC" offset like "GMT+5:30")
    if (rawShort && !/^(GMT|UTC)[+-]/i.test(rawShort)) {
      return rawShort;
    }

    // Fallback: parse parenthesised timezone name from toTimeString()
    // e.g. "14:51:00 GMT+0530 (India Standard Time)" → "IST"
    const str = d.toTimeString();
    const nameMatch = str.match(/\(([^)]+)\)/);
    if (nameMatch) {
      const words = nameMatch[1].trim().split(/\s+/).filter(Boolean);
      if (words.length > 1) {
        return words.map((w) => w[0].toUpperCase()).join("");
      }
      return words[0] ?? "";
    }

    // Last resort: raw GMT offset
    const offsetMatch = str.match(/GMT[+-]\d{2,4}/);
    return offsetMatch ? offsetMatch[0] : "";
  } catch {
    return "";
  }
};

/**
 * Formats a Date into "MM/DD/YYYY H:MM AM/PM TZ" (e.g. "04/17/2026 6:42 AM PT").
 * - Month and day are zero-padded (MM/DD).
 * - Hour has NO leading zero (6, not 06).
 * - Timezone abbreviation appended at the end.
 */
const formatDateParts = (date: Date, timeZone?: string): string => {
  const opts: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",   // "numeric" → no leading zero (6, not 06)
    minute: "2-digit",
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  };

  const parts = new Intl.DateTimeFormat("en-US", opts).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";

  const month = get("month");   // "04"
  const day = get("day");       // "17"
  const year = get("year");     // "2026"
  const hour = get("hour");     // "6"  (no leading zero with "numeric")
  const minute = get("minute"); // "42"
  const period = get("dayPeriod"); // "AM" / "PM"
  const tzAbbr = getTimezoneAbbr(date);

  const datePart = `${month}/${day}/${year}`;
  const timePart = `${hour}:${minute} ${period}`;
  return tzAbbr ? `${datePart} ${timePart} ${tzAbbr}` : `${datePart} ${timePart}`;
};

/**
 * Same as formatDateParts but omits the year — returns "MM/DD H:MM AM/PM TZ"
 * e.g. "04/17 6:42 AM PT". Used for compact timestamps in activity and notes lists.
 */
const formatDatePartsNoYear = (date: Date, timeZone?: string): string => {
  const opts: Intl.DateTimeFormatOptions = {
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  };

  const parts = new Intl.DateTimeFormat("en-US", opts).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";

  const month = get("month");
  const day = get("day");
  const hour = get("hour");
  const minute = get("minute");
  const period = get("dayPeriod");
  const tzAbbr = getTimezoneAbbr(date);

  const datePart = `${month}/${day}`;
  const timePart = `${hour}:${minute} ${period}`;
  return tzAbbr ? `${datePart} ${timePart} ${tzAbbr}` : `${datePart} ${timePart}`;
};

/**
 * Converts a UTC/ISO date string to the user's local timezone and formats it
 * as "MM/DD/YYYY H:MM AM/PM TZ" — e.g. "04/17/2026 6:42 AM PT".
 *
 * Appends 'Z' when no timezone designator is present so JavaScript always
 * parses the value as UTC, not as local time.
 *
 * @param dateString - UTC/ISO date string from the API
 * @returns Formatted local-time string, e.g. "04/17/2026 6:42 AM PT"
 */
export const formatDateWithTimezone = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";

  try {
    // Normalise to explicit UTC: append 'Z' when no timezone designator is present
    const hasTimezoneInfo = /[Z]$|[+-]\d{2}:?\d{2}$/.test(dateString.trim());
    const utcString = hasTimezoneInfo ? dateString.trim() : dateString.trim() + "Z";

    const date = new Date(utcString);
    if (isNaN(date.getTime())) return "-";

    const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return formatDateParts(date, localTz);
  } catch {
    return "-";
  }
};

/**
 * Converts a UTC/ISO date string OR a pre-formatted "MM/DD/YYYY H:MM AM/PM TZ" string
 * to a compact "MM/DD H:MM AM/PM TZ" form (no year).
 *
 * Two strategies:
 *  1. If the input looks like an ISO/UTC date string → parse and reformat without year.
 *  2. If the input looks like an already-formatted date (contains "/") → strip the year
 *     by removing the "/YYYY" portion so "04/17/2026 6:42 AM PT" → "04/17 6:42 AM PT".
 *  3. If the input is any other non-empty string (e.g. "Just now", "2 min ago") → return as-is.
 */
export const formatDateWithTimezoneNoYear = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";

  const trimmed = dateString.trim();
  if (!trimmed) return "-";

  // Strategy 1: looks like an ISO/UTC string (contains T or ends with Z or offset)
  const isIsoLike = /^\d{4}-\d{2}-\d{2}/.test(trimmed) ||
    /[Z]$|[+-]\d{2}:?\d{2}$/.test(trimmed);

  if (isIsoLike) {
    try {
      const hasTimezoneInfo = /[Z]$|[+-]\d{2}:?\d{2}$/.test(trimmed);
      const utcString = hasTimezoneInfo ? trimmed : trimmed + "Z";
      const date = new Date(utcString);
      if (!isNaN(date.getTime())) {
        const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return formatDatePartsNoYear(date, localTz);
      }
    } catch {
      // fall through
    }
  }

  // Strategy 2: already-formatted string like "04/17/2026 6:42 AM PT"
  // Strip "/YYYY" (a 4-digit year between two slashes or after the second slash)
  const withoutYear = trimmed.replace(/\/\d{4}(?=\s)/, "");
  if (withoutYear !== trimmed) {
    // Year was successfully stripped
    return withoutYear;
  }

  // Strategy 3: relative/plain string ("Just now", "2 min ago", etc.) — return as-is
  return trimmed;
};

/**
 * Formats a local datetime-local value (already in local time — no UTC shift)
 * as "MM/DD/YYYY H:MM AM/PM TZ" — e.g. "04/17/2026 6:42 AM PT".
 * Use this for values bound to datetime-local inputs.
 */
export const formatLocalDateTimeWithTimezone = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";

  try {
    const date = new Date(dateString.trim());
    if (isNaN(date.getTime())) return "-";
    return formatDateParts(date);
  } catch {
    return "-";
  }
};
